import { Box, SvgIcon, Typography } from '@mui/material';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import {
  Button,
  Loader,
  MainSectionLayout,
  TextFieldWrapperWithInputMask,
} from '@Components/index';
import commonConstants from '@Constants/common.constants';
import { CommonConstants, Icons } from '@Constants/index';
import { StyledStack } from '@Containers/CallerIdChecker/CallerIdsChecker.styles';
import { StyledForm } from '@Containers/UserCallerIds/UserCallerIds.styles';
import {
  CheckCallerIdFileResponse,
  CheckCallerIdInput,
  CheckCallerIdResponse,
  VoiceProvider,
} from '@Models/callerIds.models';
import {
  useCheckCallerIdFileMutation,
  useCheckCallerIdMutation,
} from '@Services/userCallerIds.service';
import { BaseAlertProps } from '@Typings/global.types';
import { formatNumber, unformatUSNumber } from '@Utils/common/common.utils';
import useAlert from '@Utils/hooks/useAlert.hook';

const CallerIdChecker: FunctionComponent = (): JSX.Element => {
  const { t } = useTranslation();

  const headingText = t('callerIdChecker.Caller Id Checker');

  /**
   * Check single caller Id Form Methods: Handles form validation using useForm hook
   */
  const methods = useForm<CheckCallerIdInput>({
    // resolver: yupResolver(AddNewCallerIdSchema()),
    mode: 'onChange',
    shouldUnregister: true,
  });

  const [checkCallerId, checkCallerIdMethods] = useCheckCallerIdMutation();
  const [checkCallerIdFile, checkCallerIdFileMethods] =
    useCheckCallerIdFileMutation();

  const isSingleCallerIdCheckLoading = checkCallerIdMethods.isLoading;
  const isCallerIdFileCheckLoading = checkCallerIdFileMethods.isLoading;

  const [singleCallerIdCheckResult, setSingleCallerIdCheckResult] =
    useState<CheckCallerIdResponse>();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [checkCallerIdFileResult, setCheckCallerIdFileResult] = useState<
    CheckCallerIdFileResponse | undefined
  >(undefined);

  const { triggerAlert } = useAlert();
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });

  useEffect(() => {
    if (checkCallerIdMethods.error) {
      triggerAlert(checkCallerIdMethods.error, setAlertState);
      setSingleCallerIdCheckResult(undefined);
    }
    if (checkCallerIdMethods.isSuccess) {
      setSingleCallerIdCheckResult(checkCallerIdMethods.data);
    }
  }, [
    checkCallerIdMethods.isSuccess,
    checkCallerIdMethods.error,
    checkCallerIdMethods.data,
    triggerAlert,
    checkCallerIdMethods,
  ]);

  /**
   * Check single caller Id Form Methods: Updates the form state on submit
   */
  const onSubmitHandler: SubmitHandler<CheckCallerIdInput> = (formData) => {
    if (formData) {
      const formattedPhone = formData.phone;
      let planePhone: string;

      const filteredDigits = formattedPhone.replace(/\D/g, '');

      if (filteredDigits.startsWith('0')) {
        planePhone = `+38${unformatUSNumber(formattedPhone)}`;
      } else {
        planePhone = `+1${unformatUSNumber(formattedPhone)}`;
      }

      if (filteredDigits.length === commonConstants.PHONE_NUMBER_DIGITS_COUNT) {
        setSingleCallerIdCheckResult(undefined);
        return checkCallerId({ phone: planePhone });
      }

      methods.setError('phone', {
        type: 'manual',
        message: t('userCallerIds.Invalid input'),
      });
    }
  };

  const onFileUploadHandler = async () => {
    if (!selectedFile) return;
    setCheckCallerIdFileResult(undefined);

    try {
      const result = await checkCallerIdFile({ file: selectedFile }).unwrap();

      if (result) {
        setCheckCallerIdFileResult(result);
      }
    } catch (error) {
      triggerAlert(
        CommonConstants.GLOBAL_MESSAGES.CHECK_CALLER_ID_FILE_FAILED,
        setAlertState
      );
    }
  };

  const CheckSingleCallerIdForm = (
    <>
      <FormProvider {...methods}>
        <StyledForm
          component="form"
          onSubmit={methods.handleSubmit(onSubmitHandler)}
        >
          <Box display="flex" gap={3} alignItems="center">
            <TextFieldWrapperWithInputMask
              name="phone"
              placeholder={`${t('callerIdChecker.Enter Caller Id')} *`}
              className="add-number-input"
              mask={commonConstants.PHONE_NUMBER_INPUT_MASK}
            />
            <Button
              type="submit"
              sx={{ maxHeight: 40 }}
              disabled={isSingleCallerIdCheckLoading}
            >
              {t('callerIdChecker.Check Caller Id')}
              {isSingleCallerIdCheckLoading && (
                <Loader size={20} sx={{ ml: 1 }} />
              )}
            </Button>
          </Box>
        </StyledForm>
      </FormProvider>
      <>
        {singleCallerIdCheckResult && (
          <>
            {singleCallerIdCheckResult.accountId ? (
              <>
                <Box display="flex" alignItems="flex-start">
                  <Typography variant="input" fontWeight="bold">
                    {`${t('Users.Account ID')}:`}&nbsp;
                  </Typography>
                  <Typography>{singleCallerIdCheckResult.accountId}</Typography>
                </Box>
                <Box display="flex" alignItems="flex-start">
                  <Typography variant="input" fontWeight="bold">
                    {`${t('callerIdChecker.User Name')}:`}&nbsp;
                  </Typography>
                  <Typography>{singleCallerIdCheckResult.userName}</Typography>
                </Box>
                <Box display="flex" alignItems="flex-start">
                  <Typography variant="input" fontWeight="bold">
                    {`${t('callerIdChecker.Is Account Active')}:`}&nbsp;
                  </Typography>
                  <Typography>
                    {singleCallerIdCheckResult.isAccountActive ? 'Yes' : 'No'}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="flex-start">
                  <Typography variant="input" fontWeight="bold">
                    {`${t('callerIdChecker.Number Voice Provider')}:`}&nbsp;
                  </Typography>
                  <Typography>
                    {singleCallerIdCheckResult.voiceProvider}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="flex-start">
                  <Typography variant="input" fontWeight="bold">
                    {`${t('callerIdChecker.Is Number From TPR Pool')}:`}&nbsp;
                  </Typography>
                  <Typography>
                    {singleCallerIdCheckResult.isTpr ? 'Yes' : 'No'}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="flex-start">
                  <Typography variant="input" fontWeight="bold">
                    {`${t('callerIdChecker.Has Dialer Product')}:`}&nbsp;
                  </Typography>
                  <Typography>
                    {singleCallerIdCheckResult.hasPowerDialer ? 'Yes' : 'No'}
                  </Typography>
                </Box>

                {singleCallerIdCheckResult.pendingDeactivation && (
                  <Box display="flex" alignItems="flex-start">
                    <Typography variant="input" fontWeight="bold">
                      {`${t('callerIdChecker.Pending Deactivation Since')}:`}
                      &nbsp;
                    </Typography>
                    <Typography>
                      {new Date(
                        Number(singleCallerIdCheckResult.pendingDeactivation)
                      ).toLocaleString('en-US', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: true,
                      })}
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <Typography variant="input" fontWeight="bold" color="red">
                {`${t('callerIdChecker.Number Not Exist')}`}
              </Typography>
            )}
          </>
        )}
      </>
    </>
  );

  const CheckCallerIdFileForm = (
    <Box display="flex" flexDirection="column" gap={2} mt={5}>
      <Typography variant="input" fontWeight="bold">
        {t('callerIdChecker.Upload CSV File')}
      </Typography>
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        sx={{
          width: '100%',
          height: '3.6rem',
          backgroundColor: '#F2F5F9',
          paddingLeft: '0.1rem',
        }}
      >
        <SvgIcon>{Icons.infoOutlined}</SvgIcon>
        <Typography variant="main">
          <Trans
            i18nKey="callerIdChecker.File Requirements"
            components={{ strong: <strong /> }}
          />
        </Typography>
      </Box>
      <Box display="flex" gap={2} alignItems="center">
        <Button
          type="button"
          onClick={() => document.getElementById('file-upload')?.click()}
          disabled={isCallerIdFileCheckLoading}
          sx={{ maxHeight: 40, maxWidth: 'max-content' }}
          icon="attachmentFlipped"
        >
          {t('callerIdChecker.Select File')}
        </Button>
        <Button
          type="button"
          onClick={onFileUploadHandler}
          disabled={!selectedFile || isCallerIdFileCheckLoading}
          sx={{
            maxHeight: 40,
            maxWidth: 'max-content',
          }}
        >
          {t('callerIdChecker.Upload')}
          {isCallerIdFileCheckLoading && <Loader size={20} sx={{ ml: 1 }} />}
        </Button>
      </Box>
      <input
        id="file-upload"
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) {
            setSelectedFile(file);
            setCheckCallerIdFileResult(undefined);
          }
        }}
      />
      {selectedFile && (
        <Typography>
          {t('callerIdChecker.Selected File', {
            fileName: selectedFile.name,
          })}
        </Typography>
      )}
    </Box>
  );

  const CheckCallerIdsForm = (
    <>
      {CheckSingleCallerIdForm}
      {CheckCallerIdFileForm}
    </>
  );

  const CallerIdTableRow = (
    accountId: string,
    userName: string,
    isAccountActive: boolean,
    callerId: string,
    voiceProvider: VoiceProvider,
    isTpr: boolean,
    hasPowerDialer: boolean,
    pendingDeactivation: string
  ): JSX.Element => (
    <StyledStack key={callerId}>
      <Typography className="account-id" variant="main">
        {accountId}
      </Typography>
      <Typography className="user-name" variant="main">
        {userName}
      </Typography>
      <Typography className="is-account-active" variant="main">
        {isAccountActive ? 'Y' : 'N'}
      </Typography>
      <Typography className="phone-number" variant="main">
        {/* eslint-disable-next-line react/destructuring-assignment */}
        {formatNumber(callerId)}
      </Typography>
      <Typography className="voice-provider" variant="main">
        {voiceProvider}
      </Typography>
      <Typography className="is-tpr" variant="main">
        {isTpr ? 'Y' : 'N'}
      </Typography>
      <Typography className="has-power-dialer" variant="main">
        {hasPowerDialer ? 'Y' : 'N'}
      </Typography>
      <Typography className="pending-deactivation" variant="main">
        {pendingDeactivation
          ? new Date(Number(pendingDeactivation)).toLocaleString('en-US', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true,
            })
          : '-'}
      </Typography>
    </StyledStack>
  );

  const TableComponent = (
    <>
      <Box sx={{ overflowX: 'auto', minWidth: '100%' }}>
        {checkCallerIdFileResult &&
          checkCallerIdFileResult.checkResults?.length > 0 && (
            <StyledStack id="id">
              <Typography
                className="account-id"
                variant="main"
                fontWeight={600}
              >
                {t('Users.Account ID')}
              </Typography>
              <Typography className="user-name" variant="main" fontWeight={600}>
                {t('callerIdChecker.User Name')}
              </Typography>
              <Typography
                className="is-account-active"
                variant="main"
                fontWeight={600}
              >
                {t('callerIdChecker.Is Account Active')}
              </Typography>
              <Typography
                className="phone-number"
                variant="main"
                fontWeight={600}
              >
                {t('callerIdChecker.Phone Number')}
              </Typography>
              <Typography
                className="voice-provider"
                variant="main"
                fontWeight={600}
              >
                {t('callerIdChecker.Number Voice Provider')}
              </Typography>
              <Typography className="is-tpr" variant="main" fontWeight={600}>
                {t('Is TPR')}
              </Typography>
              <Typography
                className="has-power-dialer"
                variant="main"
                fontWeight={600}
              >
                {t('callerIdChecker.Has Dialer Product')}
              </Typography>
              <Typography
                className="pending-deactivation"
                variant="main"
                fontWeight={600}
              >
                {t('callerIdChecker.Pending Deactivation Since')}
              </Typography>
            </StyledStack>
          )}
        {checkCallerIdFileResult &&
          checkCallerIdFileResult.checkResults?.length > 0 &&
          checkCallerIdFileResult?.checkResults.map(
            (item: CheckCallerIdResponse): JSX.Element =>
              CallerIdTableRow(
                item.accountId,
                item.userName,
                item.isAccountActive,
                item.callerIdToCheck,
                item.voiceProvider,
                item.isTpr,
                item.hasPowerDialer,
                item.pendingDeactivation
              )
          )}
        {checkCallerIdFileResult &&
          checkCallerIdFileResult.checkResults?.length === 0 && (
            <Typography
              variant="main"
              fontWeight="bold"
              textAlign="center"
              sx={{ my: 3.2, color: 'action.disabled' }}
            >
              {t('common.table.No data to show')}
            </Typography>
          )}
      </Box>
    </>
  );

  return (
    <>
      <MainSectionLayout
        heading={headingText}
        filters={CheckCallerIdsForm}
        alertState={alertState}
        table={TableComponent}
      />
    </>
  );
};

export default CallerIdChecker;
